import type { PublicRequestableEvent } from './rta-api.ts';
import type { CountryCode } from 'libphonenumber-js';
import { getValidPhone } from './getPhone.ts';
import { reportRtaClientFailure, type RtaFailureReporter } from './rta-observability.ts';
import {
  extractEmailDomainSuggestion,
  type EmailDomainSuggestion,
} from './emailDomainSuggestion.ts';

const SUBMIT_RTA_MUTATION = `
  mutation SubmitEventAttendanceRequests($input: SubmitEventAttendanceRequestsInput!) {
    submitEventAttendanceRequests(input: $input) {
      ok
      message
    }
  }
`;

/** Applicant fields accepted by the public request-to-attend mutation. */
export interface RtaApplicant {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
}

/** Form data submitted with one canonical requestable selection. */
export interface RtaRequestInput {
  applicant: RtaApplicant;
  /** Selected country used to normalize a national-format phone number. */
  phoneCountry?: CountryCode;
  website: string;
}

/** Injectable network and reCAPTCHA dependencies for an RTA submission. */
export interface RtaSubmitDependencies {
  endpoint: string;
  signal: AbortSignal;
  getRecaptchaToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
  reportFailure?: RtaFailureReporter;
}

/** Safe outcomes returned to the booking widget form. */
export type RtaSubmitResult =
  | {
      outcome: 'sent';
      /**
       * Non-blocking email-domain hint (a typo suggestion, or "we couldn't
       * double-check this right now") [GRO-6483]. Never blocks the
       * submission — present alongside a genuine success, not instead of
       * one. Plumbed through here; no UI currently renders it (see GRO-6429
       * for the same split on the rejection path's suggestion UI).
       */
      advisory?: string;
    }
  | {
      outcome: 'failed';
      reason: string;
      emailDomainSuggestion?: EmailDomainSuggestion;
      /**
       * Whether `reason` describes the EMAIL rather than the submission.
       *
       * Separate from `emailDomainSuggestion` because a domain can be
       * undeliverable with nothing to suggest (an unknown domain, or a Null MX
       * record). The form needs to retire those messages when the address changes
       * too, and a suggestion alone cannot tell it which messages those are.
       */
      emailRejected?: boolean;
    }
  | { outcome: 'aborted' };

/** Generic copy for transport, malformed-response, and masked server failures. */
export const GENERIC_RTA_ERROR = 'Something went wrong — please try again.';

const RECAPTCHA_ERROR = 'Unable to verify your submission. Please try again.';

/**
 * Whether a GraphQL error's `extensions` say the failure is about the email.
 *
 * Two independent signals, because either can arrive alone. The API attaches
 * `email_domain` when it has a correction to offer, and Lighthouse's `validation`
 * map names the field — a domain rejection with nothing to suggest carries only
 * the second, which is exactly the case a suggestion check alone misses.
 *
 * The validation key is matched by SUFFIX, not equality: this mutation takes an
 * input object, so the real payload reads `validation: { 'input.email': [...] }`
 * (see the fixture in rta-submit.test.ts, taken from a real response). An exact
 * `'email'` check silently matches nothing here.
 *
 * A malformed `email_domain` still counts. The API only sends that key for a
 * domain rejection, so the message is about the email whether or not its payload
 * survived — and the form's job is to retire that message when the address
 * changes, which does not depend on the suggestion being usable.
 *
 * Total over unknown input: never throws, matching
 * `extractEmailDomainSuggestion`'s contract.
 */
const isEmailFieldError = (extensions: unknown): boolean => {
  if (typeof extensions !== 'object' || extensions === null) return false;

  const record = extensions as Record<string, unknown>;
  if ('email_domain' in record) return true;

  const validation = record.validation;
  if (typeof validation !== 'object' || validation === null) return false;

  return Object.keys(validation as Record<string, unknown>).some(
    (field) => field === 'email' || field.endsWith('.email')
  );
};

const resolveServerError = (
  errors: { message?: unknown }[] | undefined,
  fallback: unknown
): string => {
  const firstMessage = errors?.[0]?.message;
  const message =
    typeof firstMessage === 'string'
      ? firstMessage
      : typeof fallback === 'string'
        ? fallback
        : null;

  if (!message || /internal server error/i.test(message)) return GENERIC_RTA_ERROR;
  if (/rate limit/i.test(message)) {
    return 'Too many attempts — give it a minute and try again.';
  }

  return message;
};

/**
 * Submit one canonical event/inventory pair in one public GraphQL POST.
 * The function obtains exactly one reCAPTCHA token and fails closed when the
 * token, response status, JSON, GraphQL channel, or one-item result is invalid.
 */
export async function submitRtaRequest(
  selection: PublicRequestableEvent,
  input: RtaRequestInput,
  dependencies: RtaSubmitDependencies
): Promise<RtaSubmitResult> {
  const fetchImpl = dependencies.fetchImpl ?? fetch;
  const reportFailure = dependencies.reportFailure ?? reportRtaClientFailure;
  const parsedPhone = getValidPhone(input.applicant.phone, input.phoneCountry);
  if (!parsedPhone) {
    return { outcome: 'failed', reason: 'Enter a valid phone number.' };
  }
  const normalizedApplicant: RtaApplicant = {
    ...input.applicant,
    phone: parsedPhone.number,
  };

  try {
    const recaptchaToken = await dependencies.getRecaptchaToken();
    if (dependencies.signal.aborted) return { outcome: 'aborted' };
    if (!recaptchaToken) {
      reportFailure({ operation: 'submission', kind: 'recaptcha' });
      return { outcome: 'failed', reason: RECAPTCHA_ERROR };
    }

    const response = await fetchImpl(dependencies.endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-dorsia-client': 'rta' },
      signal: dependencies.signal,
      body: JSON.stringify({
        query: SUBMIT_RTA_MUTATION,
        variables: {
          input: {
            selections: [
              {
                eventId: selection.event_id,
                eventInventoryId: selection.event_inventory_id,
              },
            ],
            applicant: normalizedApplicant,
            answers: null,
            recaptchaToken,
            website: input.website,
          },
        },
      }),
    });

    if (dependencies.signal.aborted) return { outcome: 'aborted' };
    if (!response.ok) {
      reportFailure({ operation: 'submission', kind: 'http', status: response.status });
      return { outcome: 'failed', reason: GENERIC_RTA_ERROR };
    }

    const payload = (await response.json()) as {
      data?: { submitEventAttendanceRequests?: unknown };
      errors?: { message?: unknown; extensions?: unknown }[];
    };
    if (dependencies.signal.aborted) return { outcome: 'aborted' };

    if (payload.errors !== undefined) {
      if (!Array.isArray(payload.errors)) {
        reportFailure({ operation: 'submission', kind: 'malformed' });
        return { outcome: 'failed', reason: GENERIC_RTA_ERROR };
      }
      if (payload.errors.length > 0) {
        reportFailure({ operation: 'submission', kind: 'graphql' });
        // GraphQL variant of the shared contract [GRO-6465]: the suggestion
        // rides in the first error's `extensions.email_domain`, not the
        // top-level payload. Absent or malformed -> null, never throws.
        const extensions = payload.errors[0]?.extensions;
        const emailDomainSuggestion = extractEmailDomainSuggestion(extensions);
        return {
          outcome: 'failed',
          reason: resolveServerError(payload.errors, null),
          ...(emailDomainSuggestion ? { emailDomainSuggestion } : {}),
          // Marked even when there is no suggestion: the form has to retire this
          // message when the address changes, and a suggestion alone cannot tell it
          // which messages belong to the email.
          ...(isEmailFieldError(extensions) ? { emailRejected: true } : {}),
        };
      }
    }

    const results = payload.data?.submitEventAttendanceRequests;
    if (!Array.isArray(results) || results.length !== 1) {
      reportFailure({ operation: 'submission', kind: 'malformed' });
      return { outcome: 'failed', reason: GENERIC_RTA_ERROR };
    }

    const result = results[0];
    if (typeof result !== 'object' || result === null || typeof result.ok !== 'boolean') {
      reportFailure({ operation: 'submission', kind: 'malformed' });
      return { outcome: 'failed', reason: GENERIC_RTA_ERROR };
    }

    return result.ok
      ? {
          outcome: 'sent',
          ...(typeof result.message === 'string' ? { advisory: result.message } : {}),
        }
      : { outcome: 'failed', reason: resolveServerError(undefined, result.message) };
  } catch {
    if (dependencies.signal.aborted) return { outcome: 'aborted' };
    reportFailure({ operation: 'submission', kind: 'transport' });
    return { outcome: 'failed', reason: GENERIC_RTA_ERROR };
  }
}

/** Result of starting or rejecting a guarded client-side submission. */
export type RtaExecutionResult<T> = { outcome: 'completed'; value: T } | { outcome: 'ignored' };

/**
 * Synchronous double-submit latch with an abortable operation lifetime.
 * A form owns one controller, calls `run` for submits, and aborts on unmount.
 */
export class RtaSubmissionController {
  private inFlight = false;
  private activeController: AbortController | null = null;

  /** Run an operation unless another is already in flight. */
  async run<T>(operation: (signal: AbortSignal) => Promise<T>): Promise<RtaExecutionResult<T>> {
    if (this.inFlight) return { outcome: 'ignored' };

    this.inFlight = true;
    this.activeController = new AbortController();

    try {
      const value = await operation(this.activeController.signal);
      return { outcome: 'completed', value };
    } finally {
      this.inFlight = false;
      this.activeController = null;
    }
  }

  /** Abort the current operation, if any. */
  abort(): void {
    this.activeController?.abort();
  }
}
